import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Hand,
  Maximize,
  Mic,
  MicOff,
  Minimize,
  MonitorUp,
  PhoneOff,
  Users,
  Video,
  VideoOff,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const MEET_BG = '#000000';
const MEET_TILE = '#1a1a1a';
const MEET_TOOLBAR = '#0d0d0d';
const MEET_BTN = '#2a2a2a';
const MEET_BTN_HOVER = '#3a3a3a';
const MEET_RED = '#e53935';
const MEET_RED_HOVER = '#c62828';
const MEET_HOST = '#7c3aed';

const AVATAR_COLORS = [
  '#8ab4f8',
  '#f28b82',
  '#fdd663',
  '#81c995',
  '#ff8bcb',
  '#78d9ec',
  '#d7aefb',
  '#aecbfa',
];

function initialsFromName(name) {
  return String(name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function avatarColorFromName(name) {
  let hash = 0;
  const str = String(name || '');
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function MeetAudioActivityIndicator({ isMuted, isSpeaking, mini = false }) {
  const maxHeight = mini ? 10 : 12;
  const idleHeights = [0.25, 0.35, 0.45, 0.3];
  const speakingHeights = [0.45, 0.75, 1, 0.65];

  if (isMuted) {
    return (
      <span
        title="Mikrofoni është i fikur"
        className="rounded-md bg-[#ea4335] p-1.5 inline-flex items-center justify-center"
      >
        <MicOff className="h-3 w-3 text-white" />
      </span>
    );
  }

  const heights = isSpeaking ? speakingHeights : idleHeights;

  return (
    <span
      title={isSpeaking ? 'Duke folur...' : 'Në dëgjim'}
      className="rounded-md bg-black/60 px-1.5 py-1 inline-flex items-center"
    >
      <span className="flex items-end gap-0.5" style={{ height: `${maxHeight}px` }}>
        {heights.map((height, index) => (
          <span
            key={index}
            className={cn(
              'w-1 rounded-full transition-all duration-150',
              isSpeaking ? 'bg-[#34a853] animate-pulse' : 'bg-[#9aa0a6]'
            )}
            style={{
              height: `${height * maxHeight}px`,
              animationDelay: isSpeaking ? `${index * 0.12}s` : undefined,
            }}
          />
        ))}
      </span>
    </span>
  );
}

function MeetToolbarButton({
  icon: Icon,
  label,
  off = false,
  danger = false,
  active = false,
  onClick,
  disabled = false,
  badge,
  className,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        'group relative flex h-11 w-11 items-center justify-center rounded-full transition-all duration-150 disabled:opacity-40',
        className
      )}
      style={{
        backgroundColor: danger
          ? 'transparent'
          : off
          ? MEET_RED
          : active
          ? MEET_HOST
          : MEET_BTN,
      }}
      onMouseEnter={(e) => {
        if (disabled || danger) return;
        if (!off && !active) e.currentTarget.style.backgroundColor = MEET_BTN_HOVER;
      }}
      onMouseLeave={(e) => {
        if (disabled || danger) return;
        e.currentTarget.style.backgroundColor = off
          ? MEET_RED
          : active
          ? MEET_HOST
          : MEET_BTN;
      }}
    >
      <Icon className="h-5 w-5 text-white" />
      {badge != null && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#7c3aed] px-1 text-[10px] font-bold text-white shadow-sm">
          {badge}
        </span>
      )}
      {/* Tooltip on hover */}
      <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#2a2a2a] px-2.5 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 z-50">
        {label}
      </span>
    </button>
  );
}

function ParticipantVideo({ stream, mirrored = false, muted = mirrored, className }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (stream) {
      el.srcObject = stream;
      el.play().catch(() => {});
    } else {
      el.srcObject = null;
    }
  }, [stream]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={muted}
      className={cn(
        'h-full w-full bg-black object-cover',
        mirrored && 'scale-x-[-1]',
        className
      )}
    />
  );
}

function MinimizedCallWidget({
  peerName,
  durationSeconds,
  isMuted,
  isCameraOn,
  localCameraStream,
  onMaximize,
  onToggleMute,
  onToggleCamera,
  onEnd,
}) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const handlePointerDown = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    setPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      return () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };
    }
  }, [isDragging, handlePointerMove, handlePointerUp]);

  const initials = initialsFromName(peerName);
  const color = avatarColorFromName(peerName);

  return (
    <div
      onPointerDown={handlePointerDown}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        backgroundColor: MEET_TOOLBAR,
      }}
      className="fixed bottom-5 right-5 z-[210] w-72 rounded-2xl border border-white/10 shadow-2xl overflow-hidden cursor-move select-none animate-in slide-in-from-bottom-4"
    >
      <div className="relative h-36 bg-[#1a1a1a] flex items-center justify-center overflow-hidden">
        {isCameraOn && localCameraStream ? (
          <ParticipantVideo stream={localCameraStream} mirrored className="h-full w-full" />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-zinc-950 shadow-md ring-2 ring-white/10"
              style={{ backgroundColor: color }}
            >
              {initials}
            </div>
            <p className="text-xs font-semibold text-white/90 truncate max-w-[200px]">
              {peerName}
            </p>
          </div>
        )}

        {/* Top bar inside mini player */}
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-xs font-semibold text-white/90 pointer-events-none">
          <span className="rounded-md bg-black/60 px-2 py-0.5 text-[10px] tracking-wide font-mono">
            {formatDuration(durationSeconds)}
          </span>
          <button
            type="button"
            onClick={onMaximize}
            className="pointer-events-auto rounded-lg bg-black/60 p-1 text-white/80 hover:bg-black/90 hover:text-white"
            title="Zgjero në ekran të plotë"
          >
            <Maximize size={14} />
          </button>
        </div>
      </div>

      {/* Mini Controls */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-[#0d0d0d] border-t border-white/5">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onToggleMute}
            className={cn(
              'rounded-full p-2 transition',
              isMuted ? 'bg-[#ea4335] text-white' : 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a]'
            )}
            title={isMuted ? 'Mikrofoni i çaktivizuar' : 'Mikrofoni aktiv'}
          >
            {isMuted ? <MicOff size={15} /> : <Mic size={15} />}
          </button>

          <button
            type="button"
            onClick={onToggleCamera}
            className={cn(
              'rounded-full p-2 transition',
              !isCameraOn ? 'bg-[#ea4335] text-white' : 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a]'
            )}
            title={isCameraOn ? 'Kamera aktive' : 'Kamera e fikur'}
          >
            {isCameraOn ? <Video size={15} /> : <VideoOff size={15} />}
          </button>
        </div>

        <button
          type="button"
          onClick={onEnd}
          className="flex items-center gap-1.5 rounded-full bg-[#e53935] px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#c62828] transition"
        >
          <PhoneOff size={14} />
          <span>Mbyll</span>
        </button>
      </div>
    </div>
  );
}

export function CallPanel({
  open,
  onClose,
  peerName = 'Student',
  peerProfession = 'Mësim Gjermanisht',
  isGroup = false,
  members = [],
  localMediaStream,
  remoteMediaStream,
  screenMediaStream,
  isMuted: controlledMuted,
  isCameraOn: controlledCameraOn,
  isScreenSharing: controlledScreenSharing,
  onToggleMute,
  onToggleCamera,
  onToggleScreenShare,
  callError,
}) {
  const [minimized, setMinimized] = useState(false);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [localIsMuted, setLocalIsMuted] = useState(false);
  const [localIsCameraOn, setLocalIsCameraOn] = useState(false);
  const [localIsScreenSharing, setLocalIsScreenSharing] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(true);

  // WebRTC Streams
  const [localCameraStream, setLocalCameraStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const isMuted = controlledMuted ?? localIsMuted;
  const isCameraOn = controlledCameraOn ?? localIsCameraOn;
  const isScreenSharing = controlledScreenSharing ?? localIsScreenSharing;
  const currentScreenStream = screenMediaStream || screenStream;
  const cameraStream = localMediaStream?.getVideoTracks?.().length
    ? localMediaStream
    : localCameraStream;
  const remoteVideoStream = remoteMediaStream?.getVideoTracks?.().length
    ? remoteMediaStream
    : null;
  const remoteAudioRef = useRef(null);

  useEffect(() => {
    const audio = remoteAudioRef.current;
    if (!audio) return undefined;
    audio.srcObject = remoteMediaStream || null;
    if (remoteMediaStream) {
      audio.play().catch((error) => {
        console.warn('Remote call audio playback was blocked:', error);
      });
    }
    return () => {
      audio.srcObject = null;
    };
  }, [remoteMediaStream]);

  // Call timer
  useEffect(() => {
    if (!open) {
      setDurationSeconds(0);
      return;
    }
    const interval = setInterval(() => {
      setDurationSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [open]);

  // Handle simulated audio activity
  useEffect(() => {
    if (!open || isMuted) {
      setIsSpeaking(false);
      return;
    }
    const interval = setInterval(() => {
      setIsSpeaking(Math.random() > 0.4);
    }, 1600);
    return () => clearInterval(interval);
  }, [open, isMuted]);

  // Clean up media streams on unmount or close
  const cleanupStreams = () => {
    if (localCameraStream) {
      localCameraStream.getTracks().forEach((track) => track.stop());
      setLocalCameraStream(null);
    }
    if (screenStream) {
      screenStream.getTracks().forEach((track) => track.stop());
      setScreenStream(null);
    }
    setLocalIsCameraOn(false);
    setLocalIsScreenSharing(false);
  };

  const handleEndCall = () => {
    cleanupStreams();
    setMinimized(false);
    onClose?.();
  };

  // Toggle Camera with real getUserMedia
  const handleToggleCamera = async () => {
    if (onToggleCamera) {
      await onToggleCamera();
      return;
    }
    if (isCameraOn) {
      if (localCameraStream) {
        localCameraStream.getVideoTracks().forEach((t) => t.stop());
      }
      setLocalIsCameraOn(false);
      setLocalCameraStream(null);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        setLocalCameraStream(stream);
        setLocalIsCameraOn(true);
      } catch (err) {
        console.warn('Could not access camera:', err);
      }
    }
  };

  // Toggle Screen Share with real getDisplayMedia
  const handleToggleScreenShare = async () => {
    if (onToggleScreenShare) {
      await onToggleScreenShare();
      return;
    }
    if (isScreenSharing) {
      if (screenStream) {
        screenStream.getTracks().forEach((t) => t.stop());
      }
      setLocalIsScreenSharing(false);
      setScreenStream(null);
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
        });
        setScreenStream(stream);
        setLocalIsScreenSharing(true);
        stream.getVideoTracks()[0].onended = () => {
          setLocalIsScreenSharing(false);
          setScreenStream(null);
        };
      } catch (err) {
        console.warn('Screen share canceled or unsupported:', err);
      }
    }
  };

  const handleToggleMute = () => {
    if (onToggleMute) {
      void onToggleMute();
      return;
    }
    setLocalIsMuted((prev) => !prev);
    if (localCameraStream) {
      localCameraStream.getAudioTracks().forEach((t) => {
        t.enabled = isMuted;
      });
    }
  };

  if (!open) return null;

  const peerInitials = initialsFromName(peerName);
  const peerColor = avatarColorFromName(peerName);

  return createPortal(
    <>
      {/* Minimized PiP View */}
      {minimized && (
        <MinimizedCallWidget
          peerName={peerName}
          durationSeconds={durationSeconds}
          isMuted={isMuted}
          isCameraOn={isCameraOn}
          localCameraStream={cameraStream}
          onMaximize={() => setMinimized(false)}
          onToggleMute={handleToggleMute}
          onToggleCamera={handleToggleCamera}
          onEnd={handleEndCall}
        />
      )}

      {/* Full Google Meet Call Dialog */}
      {!minimized && (
        <div
          className="fixed inset-0 z-[200] flex h-screen w-screen flex-col overflow-hidden select-none text-white font-sans animate-in fade-in duration-300"
          style={{ backgroundColor: MEET_BG }}
        >
          <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
          {callError && (
            <p role="alert" className="absolute left-1/2 top-16 z-20 -translate-x-1/2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white shadow-lg">
              {callError}
            </p>
          )}
          {/* Top Bar */}
          <header className="relative z-10 flex h-14 shrink-0 items-center justify-between border-b border-white/10 px-4 sm:px-6">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-[#FFCE00] to-amber-500 text-zinc-950 font-black text-sm shadow-md">
                B2
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-sm sm:text-base font-bold text-white leading-tight">
                  {peerName}
                </h2>
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <span className="inline-flex items-center gap-1 text-emerald-400 font-medium">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    E Enkriptuar (E2E)
                  </span>
                  <span>•</span>
                  <span className="font-mono">{formatDuration(durationSeconds)}</span>
                </div>
              </div>
            </div>

            {/* Top Right Actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMinimized(true)}
                className="flex items-center gap-1.5 rounded-full bg-[#2a2a2a] px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-[#3a3a3a] transition"
                title="Palos në dritare lundruese"
              >
                <Minimize size={14} />
                <span className="hidden sm:inline">Palos (PiP)</span>
              </button>

              <button
                type="button"
                onClick={handleEndCall}
                className="rounded-full bg-white/10 p-2 text-white/80 hover:bg-white/20 hover:text-white transition"
                title="Mbyll dritaren"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          {/* Main Stage Area */}
          <main className="relative flex flex-1 min-h-0 items-center justify-center p-3 sm:p-6 overflow-hidden">
            {isScreenSharing && currentScreenStream ? (
              /* Screen Share View */
              <div className="relative h-full w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-[#1a1a1a] shadow-2xl flex items-center justify-center">
                <ParticipantVideo stream={currentScreenStream} className="object-contain" />
                <div className="absolute top-4 left-4 rounded-xl bg-black/75 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-md">
                  Transmetimi i ekranit në kohë reale
                </div>
              </div>
            ) : (
              /* Participant Tiles Grid (Entriks-HR Meet Style) */
              <div className="grid h-full w-full max-w-5xl grid-cols-1 gap-4 md:grid-cols-2 items-center justify-center">
                {/* Tile 1: Remote Peer */}
                <div
                  className="relative flex h-full max-h-[500px] w-full flex-col items-center justify-center overflow-hidden rounded-3xl border border-white/10 p-6 shadow-2xl transition-all"
                  style={{ backgroundColor: MEET_TILE }}
                >
                  {remoteVideoStream ? (
                    <ParticipantVideo
                      stream={remoteVideoStream}
                      muted
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <>
                      <div className="relative flex flex-col items-center gap-4">
                        <div className="relative">
                          {isSpeaking && (
                            <span
                              className="absolute inset-0 rounded-full animate-ping opacity-40"
                              style={{ backgroundColor: peerColor }}
                            />
                          )}
                          <div
                            className="relative flex h-24 w-24 sm:h-32 sm:w-32 items-center justify-center rounded-full text-3xl sm:text-4xl font-black text-zinc-950 shadow-2xl ring-4 ring-white/10"
                            style={{ backgroundColor: peerColor }}
                          >
                            {peerInitials}
                          </div>
                        </div>
                        <div className="text-center">
                          <p className="text-base sm:text-lg font-bold text-white">{peerName}</p>
                          <p className="text-xs text-white/60 mt-0.5">{peerProfession}</p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Tile Bottom Info Bar */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <span className="rounded-xl bg-black/60 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur-md">
                      {peerName}
                    </span>
                    <MeetAudioActivityIndicator isMuted={false} isSpeaking={isSpeaking} />
                  </div>
                </div>

                {/* Tile 2: Self User */}
                <div
                  className="relative flex h-full max-h-[500px] w-full flex-col items-center justify-center overflow-hidden rounded-3xl border border-white/10 shadow-2xl transition-all"
                  style={{ backgroundColor: MEET_TILE }}
                >
                  {isCameraOn && cameraStream ? (
                    <ParticipantVideo
                      stream={cameraStream}
                      mirrored
                      className="h-full w-full object-cover rounded-3xl"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-4 p-6">
                      <div className="relative flex h-24 w-24 sm:h-32 sm:w-32 items-center justify-center rounded-full bg-gradient-to-tr from-zinc-800 to-zinc-700 text-3xl sm:text-4xl font-black text-[#FFCE00] shadow-2xl ring-4 ring-white/10">
                        TI
                      </div>
                      <div className="text-center">
                        <p className="text-base sm:text-lg font-bold text-white">Ju (Nxënës)</p>
                        <p className="text-xs text-white/60 mt-0.5">
                          {isCameraOn ? 'Kamera aktive' : 'Kamera e fikur'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Tile Bottom Info Bar */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-10">
                    <span className="rounded-xl bg-black/60 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur-md">
                      Ju (Nxënës)
                    </span>
                    <MeetAudioActivityIndicator isMuted={isMuted} isSpeaking={!isMuted && isSpeaking} />
                  </div>

                  {isHandRaised && (
                    <div className="absolute top-4 right-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#f9a825] text-zinc-950 shadow-lg">
                      <Hand size={18} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Slide-over Participants Panel */}
            {showParticipants && (
              <aside className="absolute right-3 top-3 bottom-3 z-30 w-80 rounded-3xl border border-white/10 bg-[#141414] p-5 shadow-2xl flex flex-col">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Users size={16} />
                    Pjesëmarrësit (2)
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowParticipants(false)}
                    className="rounded-lg p-1 text-white/60 hover:text-white hover:bg-white/10"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto py-4 space-y-3">
                  <div className="flex items-center justify-between rounded-xl bg-white/5 p-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-zinc-950"
                        style={{ backgroundColor: peerColor }}
                      >
                        {peerInitials}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">{peerName}</p>
                        <p className="text-[10px] text-white/50">{peerProfession}</p>
                      </div>
                    </div>
                    <MeetAudioActivityIndicator isMuted={false} isSpeaking={isSpeaking} mini />
                  </div>

                  <div className="flex items-center justify-between rounded-xl bg-white/5 p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-xs font-bold text-[#FFCE00]">
                        TI
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">Ju (Nxënës)</p>
                        <p className="text-[10px] text-white/50">Lidhje aktive</p>
                      </div>
                    </div>
                    <MeetAudioActivityIndicator isMuted={isMuted} isSpeaking={!isMuted && isSpeaking} mini />
                  </div>
                </div>
              </aside>
            )}
          </main>

          {/* Bottom Toolbar (Google Meet Toolbar) */}
          <footer
            className="relative z-10 flex h-20 shrink-0 items-center justify-center gap-3 sm:gap-4 border-t border-white/10 px-4"
            style={{ backgroundColor: MEET_TOOLBAR }}
          >
            {/* Mic Toggle */}
            <MeetToolbarButton
              icon={isMuted ? MicOff : Mic}
              label={isMuted ? 'Aktivizo Mikrofonin' : 'Fik Mikrofonin'}
              off={isMuted}
              onClick={handleToggleMute}
            />

            {/* Camera Toggle */}
            <MeetToolbarButton
              icon={isCameraOn ? Video : VideoOff}
              label={isCameraOn ? 'Fik Kamerën' : 'Aktivizo Kamerën'}
              off={!isCameraOn}
              onClick={handleToggleCamera}
            />

            {/* Screen Share */}
            <MeetToolbarButton
              icon={MonitorUp}
              label={isScreenSharing ? 'Ndalo Transmetimin e Ekranit' : 'Transmeto Ekranin'}
              active={isScreenSharing}
              onClick={handleToggleScreenShare}
            />

            {/* Raise Hand */}
            <MeetToolbarButton
              icon={Hand}
              label={isHandRaised ? 'Ule Dorën' : 'Ngri Dorën'}
              active={isHandRaised}
              onClick={() => setIsHandRaised((p) => !p)}
            />

            {/* Participants */}
            <MeetToolbarButton
              icon={Users}
              label="Pjesëmarrësit"
              active={showParticipants}
              badge={2}
              onClick={() => setShowParticipants((p) => !p)}
            />

            {/* End Call Button */}
            <button
              type="button"
              onClick={handleEndCall}
              className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white transition-all shadow-lg hover:scale-105 active:scale-95"
              style={{ backgroundColor: MEET_RED }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = MEET_RED_HOVER;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = MEET_RED;
              }}
            >
              <PhoneOff className="h-4 w-4" />
              <span className="hidden sm:inline">Përfundo</span>
            </button>
          </footer>
        </div>
      )}
    </>,
    document.body
  );
}

export default CallPanel;
